import React, { useState, useMemo, memo, useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatNumber, formatTimestamp, getExchangeLogo } from '../../utils/helpers';
import { useSoundNotificationsContext } from '../../contexts/SoundNotificationsContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { trackTradeInteraction, trackFeatureUsage, trackModalOpen, trackCoinClick } from '../../utils/analytics';
import TradeSummary from '../TradeSummary/TradeSummary';
import TradePopup from '../TradePopup/TradePopup';
import WhaleSummaryModal from '../WhaleSummaryModal/WhaleSummaryModal';
import ShareModal from '../ShareModal/ShareModal';
import TradeTooltip from '../TradeTooltip/TradeTooltip';
import SoundToggle from '../SoundToggle/SoundToggle';
import { useTopCoinsByCount } from '../../hooks/useTopCoinsByCount';
import SkeletonTradeRow from '../SkeletonTradeRow/SkeletonTradeRow';
import { FILTER_DEFAULTS } from '../../hooks/useFilters';
import './WhaleTradesTable.css';

// Random GIF backgrounds for whale trades (served locally)
const WHALE_GIFS_LONG = [
  '/assets/gifs/whale-effect-1.webp',
  '/assets/gifs/whale-effect-2.webp',
  '/assets/gifs/whale-effect-3.webp'
];

const WHALE_GIFS_SHORT = [
  '/assets/gifs/whale-effect-1.webp',
  '/assets/gifs/whale-effect-2.webp'
];

// Pick random GIF from array
function pickRandomGif(side) {
  const gifs = side === 'buy' ? WHALE_GIFS_LONG : WHALE_GIFS_SHORT;
  return gifs[Math.floor(Math.random() * gifs.length)];
}

// Memoized trade row component to prevent unnecessary re-renders
const TradeRow = memo(({ trade, onClick, t }) => {
  const shouldAnimate = !trade.isHistorical && trade.tier >= 2;
  const isTier1WithGif = trade.tier === 1 && trade.dollarSize >= 5_000_000;
  const randomGif = useMemo(() => trade.randomGif || (isTier1WithGif ? pickRandomGif(trade.side) : null), [trade.symbol, trade.timestamp, trade.side, isTier1WithGif]);

  const tierClass = trade.tier >= 1 ? `tier-${trade.tier}` : '';
  const animateClass = shouldAnimate ? 'animate-tier' : '';
  const historicalClass = trade.isHistorical ? 'historical-trade' : '';

  const inlineStyle = (isTier1WithGif && randomGif) ? {
    backgroundImage: `url(${randomGif})`,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center center'
  } : { backgroundImage: 'none' };

  const sideLabel = trade.side === 'buy' ? t('whaleTrades.long') : t('whaleTrades.short');

  return (
    <a
      href="#"
      className={`position-item ${historicalClass} ${tierClass} ${animateClass}`.trim()}
      style={inlineStyle}
      onClick={(e) => { e.preventDefault(); onClick(trade, e); }}
      data-price={trade.price}
      data-timestamp={trade.timestamp}
      data-symbol={trade.symbol}
      data-exchange={trade.exchange}
      data-side={trade.side}
      data-size={trade.dollarSize}
      data-is-liquidation="false"
      data-tier={trade.tier || ''}
    >
      {/* Static icons — never animate */}
      <div className="row-static">
        <img className="exchange" src={trade.exchangeLogo || getExchangeLogo(trade.exchange)} alt={trade.exchange} title={trade.exchange} />
        {trade.logoUrl && (
          <img className="coin fade-in" src={trade.logoUrl} alt={trade.symbol} title={trade.symbol} onError={(e) => { e.target.style.display = 'none'; }} />
        )}
      </div>

      {/* Content */}
      <div className="row-content">
        <div className="row-front">
          <p className="row-coin-name">{trade.baseCoin}</p>
          <div className={`volume ${trade.dollarSize > 1_000_000 ? 'big' : ''} ${trade.side === 'buy' ? 'success' : 'danger'}`}>
            <span className="count">{formatNumber(trade.dollarSize)}</span>
            <span className="position">{sideLabel}</span>
          </div>
        </div>
      </div>
    </a>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.trade.exchange === nextProps.trade.exchange &&
    prevProps.trade.symbol === nextProps.trade.symbol &&
    prevProps.trade.timestamp === nextProps.trade.timestamp &&
    prevProps.trade.price === nextProps.trade.price &&
    prevProps.trade.tier === nextProps.trade.tier &&
    prevProps.trade.logoUrl === nextProps.trade.logoUrl
  );
});

TradeRow.displayName = 'TradeRow';

function WhaleTradesTable({ trades, top3Trades, loading, prices, formatPrice, onOpenChart, summary, selectedCoins, filters, registerCloseTooltip, onOtherTableClick, externalTopCoins }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { localizedPath } = useLanguage();
  // Sound notifications hook
  const { whaleTradesEnabled, toggleWhaleTradesSound, millionAlertEnabled, toggleMillionAlert } = useSoundNotificationsContext();

  const [selectedTrade, setSelectedTrade] = useState(null);
  const [tooltipAnchor, setTooltipAnchor] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [showSummary, setShowSummary] = useState(false);
  const [lastTradeTime, setLastTradeTime] = useState(null);
  const lastUpdateRef = useRef(null);
  const [shareModalData, setShareModalData] = useState(null);
  const [showTop3, setShowTop3] = useState(false);
  const [showTopCoins, setShowTopCoins] = useState(false);
  const top3ButtonRef = useRef(null);
  const topCoinsButtonRef = useRef(null);
  const infoHoverRef = useRef(null);

  // Fetch top coins by trade count (skip if provided externally)
  const ownTopCoins = useTopCoinsByCount(6, { enabled: !externalTopCoins });
  const topCoins = externalTopCoins || ownTopCoins.coins;
  const topCoinsLoading = externalTopCoins ? false : ownTopCoins.loading;

  // Hide tooltip and top3 dropdown on page scroll (mobile fix)
  // Only react to page-level scroll, not internal container scroll
  useEffect(() => {
    const handleScroll = (e) => {
      // Ignore scroll events from the trades scroll container
      if (scrollContainerRef.current && scrollContainerRef.current.contains(e.target)) {
        return;
      }
      // Hide info-hover tooltip
      if (infoHoverRef.current) {
        infoHoverRef.current.blur();
        infoHoverRef.current.style.pointerEvents = 'none';
        setTimeout(() => {
          if (infoHoverRef.current) {
            infoHoverRef.current.style.pointerEvents = '';
          }
        }, 100);
      }
      // Close top3 dropdown
      setShowTop3(false);
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, []);

  // Scroll management
  const scrollContainerRef = useRef(null);
  const [isAtTop, setIsAtTop] = useState(true);
  const isAtTopRef = useRef(true); // Ref to avoid stale state in scroll adjustment
  const prevTradesLengthRef = useRef(trades.length);
  const prevScrollHeightRef = useRef(0);
  const scrollPositionRef = useRef(0);

  // Track scroll position and determine if user is at top
  // Attach ONCE on mount - do NOT re-attach on trades.length change
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const atTop = container.scrollTop < 50;
      scrollPositionRef.current = container.scrollTop;
      if (atTop !== isAtTopRef.current) {
        isAtTopRef.current = atTop;
        setIsAtTop(atTop);
      }
    };

    // Set initial state
    handleScroll();
    prevScrollHeightRef.current = container.scrollHeight;

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []); // Empty dependency - attach once on mount

  // Handle scroll position when new trades are added
  // Uses useLayoutEffect to run synchronously BEFORE browser paints (prevents jitter)
  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const newTradesCount = trades.length - prevTradesLengthRef.current;

    if (newTradesCount > 0) {
      // Calculate how much content was added
      const newScrollHeight = container.scrollHeight;
      const heightAdded = newScrollHeight - prevScrollHeightRef.current;

      if (isAtTopRef.current) {
        // User is at top - keep them at top to see newest trades
        container.scrollTop = 0;
        scrollPositionRef.current = 0;
      } else if (heightAdded > 0) {
        // User is scrolled down - adjust scrollTop to keep same content visible
        // New trades push content down, so we need to add the height difference
        container.scrollTop = scrollPositionRef.current + heightAdded;
        scrollPositionRef.current = container.scrollTop;
      }
    }

    // Update refs for next comparison
    prevTradesLengthRef.current = trades.length;
    prevScrollHeightRef.current = container.scrollHeight;
  }, [trades.length]); // Only run when trades count changes

  // Scroll to top function
  const scrollToTop = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollTo({ top: 0, behavior: 'smooth' });
      setIsAtTop(true);
    }
  };

  // Calculate summary stats
  const longTrades = useMemo(() => trades.filter(t => t.side === 'buy'), [trades]);
  const shortTrades = useMemo(() => trades.filter(t => t.side === 'sell'), [trades]);
  const totalVolume = useMemo(() => trades.reduce((sum, t) => sum + t.dollarSize, 0), [trades]);
  const longPercentage = trades.length > 0 ? Math.round((longTrades.length / trades.length) * 100) : 50;

  // Register close function with parent for cross-table tooltip management
  useEffect(() => {
    if (registerCloseTooltip) {
      registerCloseTooltip(() => setSelectedTrade(null));
    }
  }, [registerCloseTooltip]);

  // Handle trade click - show popup
  const handleTradeClick = (trade, event) => {
    event.stopPropagation();
    const key = `${trade.exchange}-${trade.symbol}-${trade.timestamp}-${trade.price}`;

    trackTradeInteraction('view_details', {
      symbol: trade.symbol,
      exchange: trade.exchange,
      dollarSize: trade.dollarSize,
      side: trade.side,
      isLiquidation: false
    });

    if (onOtherTableClick) onOtherTableClick();

    // Get bounding rect of the clicked row for tooltip positioning
    const row = event.currentTarget;
    if (row) setTooltipAnchor(row.getBoundingClientRect());

    // Toggle: if same trade clicked again, collapse
    setSelectedTrade(prev => {
      const prevKey = prev ? `${prev.exchange}-${prev.symbol}-${prev.timestamp}-${prev.price}` : null;
      return prevKey === key ? null : trade;
    });
  };

  // Handle popup close
  const handlePopupClose = () => {
    setSelectedTrade(null);
  };

  // Handle Live Chart button click
  const handleLiveChart = (symbol) => {
    if (onOpenChart) {
      onOpenChart(symbol);
    }
  };

  // Handle Share button click
  const handleShare = (trade, markPrice) => {
    setShareModalData({ trade, markPrice });
    setSelectedTrade(null); // Close the popup
  };

  // Handle Coin Details button click
  const handleCoinDetails = (symbol) => {
    trackCoinClick(symbol, {
      page: '/trades',
      source: 'trade_popup',
      trade_type: 'whale_trade'
    });
    navigate(localizedPath(`/price/${symbol}`));
  };

  // Close popup when clicking outside or scrolling
  useEffect(() => {
    if (!selectedTrade) return;

    const handleClickOutside = () => {
      setSelectedTrade(null);
    };

    const handleScroll = () => {
      setSelectedTrade(null);
    };

    document.addEventListener('click', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [selectedTrade]);

  // Close top 3 dropdown when clicking outside
  useEffect(() => {
    if (!showTop3) return;

    const handleClickOutside = (event) => {
      if (top3ButtonRef.current && !top3ButtonRef.current.contains(event.target)) {
        setShowTop3(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showTop3]);

  // Debug: log selectedTrade changes
  useEffect(() => {
    console.log('✅ selectedTrade changed:', selectedTrade ? `${selectedTrade.symbol} @ ${selectedTrade.exchange}` : 'null');
  }, [selectedTrade]);

  // Update last trade time when new trades come in
  useEffect(() => {
    if (trades.length > 0) {
      // Use current time as "last received" time, not trade timestamp
      setLastTradeTime(Date.now());
    }
  }, [trades]);

  // Update "last update" text every second via direct DOM write (avoids re-render)
  useEffect(() => {
    if (!lastTradeTime) {
      if (lastUpdateRef.current) lastUpdateRef.current.textContent = '-';
      return;
    }

    const updateText = () => {
      if (!lastUpdateRef.current) return;
      const now = Date.now();
      const diffMs = now - lastTradeTime;
      const diffSec = Math.floor(diffMs / 1000);

      let text;
      if (diffSec < 60) {
        text = t('time.secondsAgo', { count: diffSec });
      } else if (diffSec < 3600) {
        text = t('time.minutesAgo', { count: Math.floor(diffSec / 60) });
      } else {
        text = t('time.hoursAgo', { count: Math.floor(diffSec / 3600) });
      }
      lastUpdateRef.current.textContent = text;
    };

    updateText();
    const interval = setInterval(updateText, 1000);

    return () => clearInterval(interval);
  }, [lastTradeTime, t]);

  if (loading) {
    return (
      <div className="card">
        <div className="card-header d-flex align-items-center justify-content-between">
          <div className="card-title mb-0">
            <p>🐳 {t('whaleTrades.title')}</p>
          </div>
        </div>
        <SkeletonTradeRow />
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header d-flex align-items-end justify-content-between flex-nowrap">
        <div className="card-title mb-0 d-flex align-items-center">
          <div className="info-hover" ref={infoHoverRef}>
            <p className="title-with-tooltip">🐳 {t('whaleTrades.title')}</p>
            <div className="tooltip-card">
              <div className="card-body">
                <h5>{t('whaleTrades.title')}</h5>
                <span className="divider"></span>
                <p>{t('whaleTrades.tooltip')}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <div className="header-actions-row">
            <span data-tour="whale-sound-toggle">
            <SoundToggle
              enabled={whaleTradesEnabled}
              onToggle={toggleWhaleTradesSound}
              label={t('whaleTrades.title')}
              hideTooltipOnMobile={false}
              millionAlertEnabled={millionAlertEnabled}
              onMillionAlertToggle={toggleMillionAlert}
              showMillionAlertToggle={true}
              tooltipContent={`
                <h4>🔔 ${t('soundTiers.whaleTitle')}</h4>
                <div class="tier-info">
                  <div class="tier-row">
                    <span class="tier-label">${t('soundTiers.tier1')}:</span>
                    <span class="tier-value">$1M - $25M 🐟</span>
                  </div>
                  <div class="tier-row">
                    <span class="tier-label">${t('soundTiers.tier2')}:</span>
                    <span class="tier-value">$25M - $50M 🐠</span>
                  </div>
                  <div class="tier-row">
                    <span class="tier-label">${t('soundTiers.tier3')}:</span>
                    <span class="tier-value">$50M - $100M 🐬</span>
                  </div>
                  <div class="tier-row">
                    <span class="tier-label">${t('soundTiers.tier4')}:</span>
                    <span class="tier-value">$100M+ 🐋</span>
                  </div>
                </div>
              `}
            />
            </span>
            <div
              ref={topCoinsButtonRef}
              style={{ position: 'relative' }}
            >
              <button
                className={`header-action-btn-small ${showTopCoins ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowTopCoins(!showTopCoins);
                  setShowTop3(false);
                }}
                title="Top coins by trade count"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                  <path d="M3 13h2v8H3v-8zm6-5h2v13H9V8zm6-5h2v18h-2V3zm6 9h2v9h-2v-9z"/>
                </svg>
              </button>
            </div>
            <div
              ref={top3ButtonRef}
              style={{ position: 'relative' }}
            >
              <button
                className="header-action-btn-small"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowTopCoins(false);
                  if (!showTop3) {
                    trackFeatureUsage('top3_trades', { page: '/trades' });
                  }
                  setShowTop3(!showTop3);
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
                </svg>
              </button>
              {showTop3 && top3Trades.length > 0 && (
                <div className="top3-dropdown">
                  {top3Trades.map((trade, index) => (
                    <div
                      key={`${trade.exchange}-${trade.symbol}-${trade.timestamp}`}
                      className="top3-dropdown-item"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowTop3(false);
                        handleTradeClick(trade, e);
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ color: '#888', fontSize: '12px', fontWeight: 'bold' }}>#{index + 1}</span>
                        {trade.logoUrl && (
                          <img
                            src={trade.logoUrl}
                            alt={trade.symbol}
                            style={{ width: '20px', height: '20px', borderRadius: '50%' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <span style={{ color: '#fff', fontSize: '12px', fontWeight: '500' }}>{trade.baseCoin}</span>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{
                          color: trade.side === 'buy' ? '#28a745' : '#dc3545',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}>
                          ${formatNumber(trade.dollarSize)}
                        </div>
                        <div style={{
                          color: trade.side === 'buy' ? '#28a745' : '#dc3545',
                          fontSize: '10px',
                          textTransform: 'uppercase'
                        }}>
                          {trade.side === 'buy' ? t('whaleTrades.long') : t('whaleTrades.short')}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button
              className="header-action-btn-small"
              onClick={() => {
                trackFeatureUsage('whale_summary', { page: '/trades' });
                setShowSummary(true);
              }}
              title="View 24h Summary"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                <path d="M2 13H8V21H2V13ZM9 3H15V21H9V3ZM16 8H22V21H16V8Z"/>
              </svg>
            </button>
          </div>
          {!isAtTop && trades.length > 0 && (
            <button
              className="scroll-top-header-btn"
              onClick={scrollToTop}
              title="Scroll to top"
              style={{ flexShrink: 0 }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                <path d="M12 4l-8 8h5v8h6v-8h5z"/>
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Compact Trade Summary */}
      <div className="compact-summary" style={{ backgroundColor: '#1a1a1a', borderTop: '1px solid #333', padding: '8px 12px', fontSize: '11px' }}>
        <div className="d-flex justify-content-between align-items-center flex-nowrap">
          <div className="d-flex align-items-center flex-nowrap">
            <span className="text-success me-2" style={{ fontWeight: 'bold' }}>{longTrades.length}</span>
            <span className="text-muted me-3 d-none d-sm-inline" style={{ fontSize: '10px' }}>{t('whaleTrades.long')}</span>
            <span className="text-danger me-2" style={{ fontWeight: 'bold' }}>{shortTrades.length}</span>
            <span className="text-muted d-none d-sm-inline" style={{ fontSize: '10px' }}>{t('whaleTrades.short')}</span>
          </div>
          <div className="d-flex align-items-center flex-nowrap">
            <div className="progress me-2" style={{ height: '8px', width: '60px', backgroundColor: '#dc3545', borderRadius: '4px' }}>
              <div className="progress-bar" style={{ width: `${longPercentage}%`, backgroundColor: '#28a745', borderRadius: '4px' }}></div>
            </div>
            <small className="text-warning" style={{ fontWeight: 'bold' }}>${formatNumber(totalVolume)}</small>
            <small className="text-muted ms-2 d-none d-md-inline" style={{ fontSize: '9px' }}>{t('whaleTrades.last')}: <span ref={lastUpdateRef}>-</span></small>
          </div>
        </div>
      </div>

      {/* Top Coins Panel - Chips with rank, symbol, count */}
      {showTopCoins && (
        <div className="top-coins-panel">
          <div className="top-coins-title">Top by Trades</div>
          {topCoinsLoading ? (
            <div className="top-coins-loading">Loading...</div>
          ) : topCoins.length > 0 ? (
            topCoins.map((coin, index) => (
              <div
                key={coin.coin}
                className="top-coin-item"
                onClick={() => {
                  trackCoinClick(coin.coin, {
                    page: '/trades',
                    source: 'top_coins_panel',
                    trade_count: coin.count
                  });
                  navigate(localizedPath(`/price/${coin.coin}`));
                }}
              >
                <span className="top-coin-rank">#{index + 1}</span>
                <span className="top-coin-symbol">{coin.coin}</span>
                <span className="top-coin-count">{coin.count.toLocaleString()}</span>
              </div>
            ))
          ) : (
            <div className="top-coins-empty">No data</div>
          )}
        </div>
      )}

      <div className="card-body scroll" ref={scrollContainerRef} data-tour="whale-trades-feed">
        {trades.length === 0 ? (
          <div className="no-records-wrapper">
            {filters && filters.whaleTradesMinVolume > FILTER_DEFAULTS.whaleTradesMinVolume ? (
              <div className="text-center py-5">
                <p className="text-muted">{t('whaleTrades.noTradesFiltered', 'No trades matching your filter.')}</p>
                <p className="text-muted" style={{fontSize: '13px', marginTop: '8px'}}>
                  {t('whaleTrades.tryLoweringFilter', 'Try lowering the minimum volume threshold.')}
                </p>
              </div>
            ) : selectedCoins && selectedCoins.length > 0 ? (
              <div className="text-center py-5">
                <p className="text-muted">{t('whaleTrades.waitingForTrades')}</p>
                <p className="text-muted" style={{fontSize: '13px', marginTop: '8px'}}>
                  <span className="coin-page-link" onClick={() => navigate(localizedPath(`/price/${selectedCoins[0]}`))} style={{color: '#5b9bd5', cursor: 'pointer', textDecoration: 'underline'}}>
                    {t('whaleTrades.visitCoinPage')}
                  </span>
                </p>
              </div>
            ) : (
              <p className="text-center text-muted py-5">{t('whaleTrades.noTrades')}</p>
            )}
          </div>
        ) : (
          trades.map((trade) => {
            const key = `${trade.exchange}-${trade.symbol}-${trade.timestamp}-${trade.price}`;
            return (
              <TradeRow
                key={key}
                trade={trade}
                onClick={handleTradeClick}
                t={t}
              />
            );
          })
        )}
      </div>

      {/* Trade Tooltip Popup */}
      {selectedTrade && (
        <TradeTooltip
          trade={selectedTrade}
          livePrice={prices?.[selectedTrade.baseCoin || selectedTrade.symbol?.replace(/USDT$|USD$/i, '')]}
          formatPrice={formatPrice}
          onClose={handlePopupClose}
          onChart={handleLiveChart}
          onShare={handleShare}
          onDetails={handleCoinDetails}
          anchorRect={tooltipAnchor}
        />
      )}

      {/* Share Modal */}
      {shareModalData && (
        <ShareModal
          trade={shareModalData.trade}
          markPrice={shareModalData.markPrice}
          onClose={() => setShareModalData(null)}
        />
      )}

      {/* Whale Summary Modal */}
      <WhaleSummaryModal
        show={showSummary}
        onClose={() => setShowSummary(false)}
        summary={summary}
      />
    </div>
  );
}

export default WhaleTradesTable;
